import { memo, useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import paymentsGlossaryMessages from 'i18n/paymentsGlossaryMessages'
import styled from 'styled-components'
import { rem } from 'polished'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import Image from 'components/Common/Image'
import TextLink from 'LuxKit/TextLink'
import CurrencyContext from 'contexts/currencyContext'
import { getBNPLMethodAvailability } from 'lib/payment/paymentUtils'
import useToggle from 'hooks/useToggle'
import InterestFreeLearnMoreModal from 'components/Common/Modals/InterestFreeLearnMoreModal/InterestFreeLearnMoreModal'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'

const Root = styled(Pane)`
  text-align: center;
`

const Logos = styled(Group)`
  margin-top: ${rem(16)};
`

const LogoImages = styled(Image)`
    width: 100%;
    max-width: ${rem(130)};
    max-height: ${rem(40)};

    ${mediaQueryUp.tablet} {
      max-width: ${rem(90)};
    }
`

interface Props {
  className?: string
  onLearnMore?: () => void
  prices: Array<number> | number
}

function OfferBNPLAd(props: Props) {
  const {
    className,
    prices,
    onLearnMore,
  } = props

  const intl = useIntl()
  const [modalOpened, onInterestFreeModalClose, showInterestFreeModal] = useToggle(false)
  const currency = useContext(CurrencyContext)

  const handleOnLearnMore = useCallback(() => {
    showInterestFreeModal()
    onLearnMore?.()
  }, [onLearnMore, showInterestFreeModal])

  const bnplMethods = useMemo(() => {
    const allPrices = Array.isArray(prices) ? prices : [prices]
    return getBNPLMethodAvailability(currency, ...allPrices)
  }, [prices, currency])
  const hasAnyMethods = useMemo(() => Object.values(bnplMethods).some(Boolean), [bnplMethods])
  const klarnaLogoAlt = intl.formatMessage({
    id: 'aaFwtzGjxWfM-8yI69du',
    defaultMessage: 'Klarna Pay',
    description: 'Alt text for the Klarna logo shown in the buy now pay later banner on the offer page',
  })
  const afterPayLogoAlt = intl.formatMessage({
    id: 'beY-CeyYQr_o3DlOy2fP',
    defaultMessage: 'After Pay',
    description: 'Alt text for the Afterpay logo shown in the buy now pay later banner on the offer page',
  })
  const shopbackLogoAlt = intl.formatMessage({
    id: 'TmS_536UwgI8QyuanVXv',
    defaultMessage: 'Shopback',
    description: 'Alt text for the ShopBack logo shown in the buy now pay later banner on the offer page',
  })
  const atomeLogoAlt = intl.formatMessage({
    id: 'zGRc-H1KevJ7WQWfleqD',
    defaultMessage: 'Atome',
    description: 'Alt text for the Atome logo shown in the buy now pay later banner on the offer page',
  })
  const luxPayLogoAlt = intl.formatMessage(paymentsGlossaryMessages.luxPay)

  if (!hasAnyMethods) {
    return null
  }

  return (<>
    <InterestFreeLearnMoreModal
      isOpen={modalOpened}
      onClose={onInterestFreeModalClose}
    />
    <Root className={className}>
      <PaneBody>
        <BodyText variant="small" weight="bold">
          {intl.formatMessage({
            id: 'bs_WUMQIqpg7TwpHyerB',
            defaultMessage: 'Interest-free payment options available',
            description: 'BNPL banner text on offer page indicating interest-free payment options',
          })}&nbsp;
          <TextLink onClick={handleOnLearnMore}>
            {intl.formatMessage({
              id: 'alfpKr-eqhSMHJYjNz7S',
              defaultMessage: 'Learn more',
              description: 'Link text to open BNPL learn more modal on offer page',
            })}
          </TextLink>
        </BodyText>
        <Logos direction="horizontal" gap={16} tabletGap={8} horizontalAlign="center" verticalAlign="center">
          {bnplMethods.isKlarnaAvailable || bnplMethods.isKlarnaBpAvailable && (
            <LogoImages id="ph5b1cik9gdapegknwl" alt={klarnaLogoAlt} />
          )}
          {bnplMethods.isAfterPayBpAvailable && (
            <LogoImages id="2kac81byj4yt5abm0nr" alt={afterPayLogoAlt} />
          )}
          {bnplMethods.isHoolahBpAvailable && (
            <LogoImages id="shopback-logo" alt={shopbackLogoAlt} />
          )}
          {bnplMethods.isAtomeBpAvailable && (
            <LogoImages id="atome-logo" alt={atomeLogoAlt} />
          )}
          {bnplMethods.isWaaveBpAvailable && (
            <LogoImages id="luxpay-logo" alt={luxPayLogoAlt} />
          )}
        </Logos>
      </PaneBody>
    </Root>
  </>

  )
}

export default memo(OfferBNPLAd)
